import moment, { Moment } from 'moment';
import { ArrayModel, ArrayModelFromJson, JsonArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';
import { DropdownModel } from './dropdown.model';
import { Fda2579FormModel, Fda2579FormModelFromJson, JsonFda2579FormModel } from './fda2579-form.model';
import { RadioModel } from './radio.model';
import { BooleanModel } from '../primitive/boolean.model';
import { DateModel } from '../primitive/date.model';
import { NumberModel } from '../primitive/number.model';
import { TextModel, TextModelFromJson } from '../primitive/text.model';
import { BooleanModelOptions } from '../options/boolean-model-options';
import { DropdownModelOptions } from '../options/dropdown-model-options';
import { NumberModelOptions } from '../options/number-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModelOptions } from '../options/text-model-options';
import { NotificationModel, ODataPaperworkModel } from '../low-level/notification.model';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { isDefined } from '../../utilities/is-defined';
import { ConfigConstants } from '../../config/config-constants';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { UserGidService } from '../../services/user-gid/user-gid.service';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import merge from 'lodash/merge';
import { TranslateService } from '@ngx-translate/core';
import { signal, WritableSignal } from '@angular/core';
import { ArrayModelOptions } from '../options/array-model-options';
import INSTALLATION_PAPERSET_CODE_TYPE = ConfigConstants.INSTALLATION_PAPERSET_CODE_TYPE;
import INSTALLATION_PAPERSET_CODE_GROUP = ConfigConstants.INSTALLATION_PAPERSET_CODE_GROUP;
import optionYes = ConfigConstants.optionYes;
import INSTALLATION_PAPERWORK_OPTIONS = ConfigConstants.INSTALLATION_PAPERWORK_OPTIONS;
import INSTALLATION_PAPERWORK_OPTIONS_YES_NA = ConfigConstants.INSTALLATION_PAPERWORK_OPTIONS_YES_NA;
import INSTALLATION_PAPERWORK_SYNGO_SOFTWARE = ConfigConstants.INSTALLATION_PAPERWORK_SYNGO_SOFTWARE;
import INSTALLATION_PAPERWORK_SITE_TYPE = ConfigConstants.INSTALLATION_PAPERWORK_SITE_TYPE;
import INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT = ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT;

const HELIUM_LEVEL_PATTERN = '^(\\d{1,4}(\\.(\\d{1,2})?)?L|\\d{1,2}(\\.(\\d{1,2})?)?%|100(\\.0?)?%)$';
const MAGNETIC_CURRENT_PATTERN = '^\\d{1,4}(\\.\\d{1,2}?)?$';
const PPWK_DATE_FORMAT = 'YYYYMMDDHHmmss';
const PUNCHLIST_MAX_STRING_LENGTH = 120;
const PUNCHLIST_MAX_ENTRIES = 10;
const CRM_CODE_MODEL_VALUE_MAX_STRING_LENGTH = 80;
const FORMS_MAX_SUBMIT_STATUS_LENGTH = 3; // Maximum number of characters that a form status can have. E.g.: "F", "I", "FNO", "INO", "H", etc.

export class ConfirmationInstallationPaperworkModel extends CompositeModel {
    public startupStatus: DropdownModel<TextModel>;
    public siemensPunchlist: ArrayModel<TextModel>;
    public customerPunchlist: ArrayModel<TextModel>;
    public isPersonLeadResponsible: RadioModel<TextModel>;
    public isSystemReadyForStartupAndDayOne: RadioModel<TextModel>;
    public dayOneAttachments: FileNameList;
    public isIVKVerified: DisplayDecorator<BooleanModel>;
    public isSerialNumberDifferentThanIVK: RadioModel<TextModel>;
    public differentSerialNumberThanIVKLongText: TextModel;
    public isSRSVerifiedBySrsTse: RadioModel<TextModel>;
    public isDICOMProvided: RadioModel<TextModel>;
    public isSafetyUICompleted: RadioModel<TextModel>;
    public finalDayAttachments: FileNameList;
    public cseHandoverMethod: DropdownModel<TextModel>;
    public cseHandoverName: DropdownModel<TextModel>;
    public cseHandoverDate: DateModel;
    public isArtisLicenseKeyRemoved: RadioModel<TextModel>;
    public isIVKsubmittedForSyngo: RadioModel<TextModel>;
    public isLeonardoLicenseKeyRemoved: RadioModel<TextModel>;
    public isSiemensSoldOEMEquipment: RadioModel<TextModel>;
    public soldOEMEquipmentList: string[];
    public isPaceStartupAttached: TextModel;
    public startupFormSubmitStatus: WritableSignal<string> = signal('');
    public attachIQAPReport: TextModel;
    public iqapReportSubmitStatus: WritableSignal<string> = signal('');
    public attachFDA2579Form: Fda2579FormModel;
    public attachFDA2579FormCompleted = signal(false);
    public isFDA2579FormCompletedOnPreviousTask: boolean;
    public isIseCseMetricFormCompleted: DisplayDecorator<BooleanModel>;
    public heliumLevelArrival: TextModel;
    public heliumLevelCompletion: TextModel;
    public isChillerFlModelSerialGiven: RadioModel<TextModel>;
    public chillerFunctionalLocation: TextModel;
    public chillerMakeModelNumber: TextModel;
    public chillerSerialNumber: TextModel;
    public magneticCurrent: NumberModel;
    public isMagnetRampedDownAtRequest: RadioModel<TextModel>;
    public isHeliumTrackerDataEntered: RadioModel<TextModel>;
    public syngoClassicOrNativeSoftware: DropdownModel<TextModel>;
    public attachSyngoSafetyRelatedReport: TextModel;
    public syngoSafetyReportSubmitStatus: WritableSignal<string> = signal('');
    public preRampPhotos: FileNameList;
    public isMRDoorSwitchOperational: RadioModel<TextModel>;
    public startupReportMR: TextModel;
    public startupReportMRSubmitStatus: WritableSignal<string> = signal('');
    public installationReport: TextModel;
    public installationReportSubmitStatus: WritableSignal<string> = signal('');
    public siteType: DropdownModel<TextModel>;
    public siteTypeComment: TextModel;
    public installationPaperworkQuestions: Record<string, ODataPaperworkModel | undefined>;
    public readonly installationPaperworkCodes = ConfigConstants.INSTALLATION_PAPERWORK_CODES;
    private notification: NotificationModel;
    private userGid: string;

    public readonly isSTPLComplete: boolean;
    public readonly isSTPCComplete: boolean;

    public constructor(notification: NotificationModel) {
        super('ConfirmationInstallationPaperworkModel');
        const codeSetSTPL = notification.codeSet.find(
            (code) => code.codeName.value === ConfigConstants.INSTALLATION_PAPERWORK_CODES.siemensPunchList,
        );
        this.isSTPLComplete = codeSetSTPL?.codeDesc?.value === ConfigConstants.INSTALLATION_PAPERSET_PUNCHLIST_COMPLETE;
        const codeSetSTPC = notification.codeSet.find(
            (code) => code.codeName.value === ConfigConstants.INSTALLATION_PAPERWORK_CODES.customerPunchList,
        );
        this.isSTPCComplete = codeSetSTPC?.codeDesc?.value === ConfigConstants.INSTALLATION_PAPERSET_PUNCHLIST_COMPLETE;

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const userService = injector.get(UserGidService);
        this.userGid = userService.getUserGid();
        this.notification = notification;
        this.installationPaperworkQuestions = notification.installationPaperworkQuestions;

        // Radio Control Default definitions:
        const radioEntry = (key: string, value: string): DisplayDecorator<TextModel> =>
            new DisplayDecorator(value, new TextModel(key, value));

        const radioOptionGroup = (key: string, values: string[]): Array<DisplayDecorator<TextModel>> =>
            values.map((value) => radioEntry(key, value));

        const yesNoNAOptions = new RadioModelOptions({
            required: true,
            initialSelection: -1,
        });
        const yesNoOptions = new RadioModelOptions({
            required: true,
            invalidSelection: 2,
            initialSelection: 2,
        });

        const startupStatusEntries = [
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SYSTEM_STARTUP_IN_PROGRESS'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupStatus,
                    ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupInProgress,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SYSTEM_STARTUP_COMPLETE'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupStatus,
                    ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupComplete,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SYSTEM_STARTUP_COMPLETE_OPEN_PUNCHLISTS'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupStatus,
                    ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists,
                ),
            ),
        ];
        this.startupStatus = new DropdownModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupStatus,
            startupStatusEntries,
            new DropdownModelOptions({ required: true }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupStatus]
                ?.Previousresponse ===
            ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS_GW_VALUE_RESPONSE.startupCompleteOpenPunchlists
        ) {
            this.startupStatus.selectByValue(
                ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists,
            );
        }
        switch (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupStatus]
                ?.Previousresponse
        ) {
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS_GW_VALUE_RESPONSE
                .startupCompleteOpenPunchlists:
                this.startupStatus.selectByValue(
                    ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists,
                );
                break;
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS_GW_VALUE_RESPONSE.startupInProgress:
                this.startupStatus.selectByValue(
                    ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupInProgress,
                );
                break;
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS_GW_VALUE_RESPONSE.startupComplete:
                this.startupStatus.selectByValue(
                    ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupComplete,
                );
                break;
        }

        this.siemensPunchlist = this.buildPunchlist(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.siemensPunchList,
            notification.siemensPunchlistEntries ?? [],
            this.isSTPLComplete,
        );
        this.customerPunchlist = this.buildPunchlist(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.customerPunchList,
            notification.customerPunchlistEntries ?? [],
            this.isSTPCComplete,
        );

        this.isPersonLeadResponsible = new RadioModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPersionLeadResponsible,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPersionLeadResponsible, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
                ConfigConstants.optionNA,
            ]),
            { ...yesNoOptions },
        );

        this.isSystemReadyForStartupAndDayOne = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSystemReadyForStartupAndDayOne,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSystemReadyForStartupAndDayOne, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
                ConfigConstants.optionNA,
            ]),
            {
                ...yesNoOptions,
            },
        );
        this.dayOneAttachments = {
            fileNames: [],
            options: {
                completed: signal(false),
                required: signal(false),
            },
        };
        if (
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSystemReadyForStartupAndDayOne
            ]?.Previousresponse === INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes]
        ) {
            this.isSystemReadyForStartupAndDayOne.selectByDisplayName(ConfigConstants.optionNo);
            this.isSystemReadyForStartupAndDayOne.options = {
                ...this.isSystemReadyForStartupAndDayOne.options,
                readonly: true,
            };
            this.dayOneAttachments.options.completed.set(true);
        }

        this.isIVKVerified = new DisplayDecorator<BooleanModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKVerified,
            new BooleanModel(
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKVerified,
                false,
                new BooleanModelOptions({ required: true, showLabel: false }),
            ),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKVerified]
                ?.Previousresponse === INSTALLATION_PAPERWORK_OPTIONS[optionYes]
        ) {
            this.isIVKVerified.value = true;
        }

        this.isSerialNumberDifferentThanIVK = new RadioModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                required: true,
                initialSelection: -1,
            }),
        );
        this.differentSerialNumberThanIVKLongText = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText,
            '',
            new TextModelOptions({
                placeholder: translate.instant('INSTALLATION_PAPERWORK_SERIAL_NUMBER_HINT'),
                scannable: true,
            }),
        );
        if (
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText
            ]?.AlreadyUploaded &&
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText
            ]?.Previousresponse
        ) {
            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText
                ].Previousresponse === INSTALLATION_PAPERWORK_OPTIONS_YES_NA[ConfigConstants.optionNA]
            ) {
                this.isSerialNumberDifferentThanIVK.selectByDisplayName(
                    INSTALLATION_PAPERWORK_OPTIONS_YES_NA[ConfigConstants.OPTION_0_NA],
                );
            } else {
                this.isSerialNumberDifferentThanIVK.selectByDisplayName(
                    INSTALLATION_PAPERWORK_OPTIONS_YES_NA[ConfigConstants.OPTION_1_YES],
                );
                this.differentSerialNumberThanIVKLongText.value =
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.differentSerialNumberThanIVKLongText
                    ].Previousresponse;
            }
        }

        this.isSRSVerifiedBySrsTse = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSRSVerifiedBySrsTse,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSRSVerifiedBySrsTse, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
                ConfigConstants.optionNA,
            ]),
            {
                ...yesNoNAOptions,
            },
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSRSVerifiedBySrsTse]
                ?.AlreadyUploaded
        ) {
            this.isSRSVerifiedBySrsTse.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSRSVerifiedBySrsTse
                    ].Previousresponse
                ],
            );
        }

        this.isDICOMProvided = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isDICOMProvided,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isDICOMProvided, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
                ConfigConstants.optionNA,
            ]),
            {
                ...yesNoNAOptions,
            },
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isDICOMProvided]
                ?.AlreadyUploaded
        ) {
            this.isDICOMProvided.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS[
                    this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isDICOMProvided]
                        .Previousresponse
                ],
            );
        }

        this.isSafetyUICompleted = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSafetyUICompleted,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSafetyUICompleted, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                required: true,
                initialSelection: -1,
                invalidSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSafetyUICompleted]
                ?.AlreadyUploaded
        ) {
            this.isSafetyUICompleted.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS_YES_NA[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSafetyUICompleted
                    ].Previousresponse
                ],
            );
        }

        this.finalDayAttachments = {
            fileNames: [],
            options: {
                completed: signal(false),
                required: signal(true),
            },
        };
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.finalDayAttachments]
                ?.AlreadyUploaded
        ) {
            this.finalDayAttachments.options.completed.set(
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.finalDayAttachments]
                    ?.Previousresponse === ConfigConstants.OPTION_1_YES,
            );
        }

        const cseHandoverMethodEntries = [
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_HANDOVER_ONSITE'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod,
                    ConfigConstants.INSTALLATION_PAPERWORK_HANDOVER_METHOD.onsite,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_HANDOVER_PHONE'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod,
                    ConfigConstants.INSTALLATION_PAPERWORK_HANDOVER_METHOD.phone,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_HANDOVER_VOICEMAIL'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod,
                    ConfigConstants.INSTALLATION_PAPERWORK_HANDOVER_METHOD.voicemail,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_HANDOVER_TEXT'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod,
                    ConfigConstants.INSTALLATION_PAPERWORK_HANDOVER_METHOD.text,
                ),
            ),
        ];
        this.cseHandoverMethod = new DropdownModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod,
            cseHandoverMethodEntries,
            new DropdownModelOptions({ required: true }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod]
                ?.AlreadyUploaded
        ) {
            this.cseHandoverMethod.selectByValue(
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod]
                    .Previousresponse,
            );
        }

        const cseHandoverAssociation = ConfigConstants.INSTALLATION_PAPERWORK_HANDOVER_ASSOCIATION.map(
            (association) =>
                new DisplayDecorator<TextModel>(
                    association,
                    new TextModel(ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverName, association),
                ),
        );
        this.cseHandoverName = new DropdownModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverName,
            cseHandoverAssociation,
            new DropdownModelOptions({
                required: true,
                minLength: 1,
                displayText: true,
            }),
        );

        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverName]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverName]
                .Previousresponse
        ) {
            this.cseHandoverName.textFieldValue =
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverName
                ].Previousresponse;
        }
        this.cseHandoverDate = new DateModel(ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverDate, undefined);
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverDate]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverDate]
                .Previousresponse
        ) {
            this.cseHandoverDate.value = moment(
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverDate]
                    .Previousresponse,
                PPWK_DATE_FORMAT,
            );
        }

        this.isArtisLicenseKeyRemoved = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isArtisLicenseKeyRemoved,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isArtisLicenseKeyRemoved, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                initialSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isArtisLicenseKeyRemoved]
                ?.AlreadyUploaded
        ) {
            this.isArtisLicenseKeyRemoved.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS_YES_NA[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isArtisLicenseKeyRemoved
                    ].Previousresponse
                ],
            );
        }

        this.isIVKsubmittedForSyngo = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKsubmittedForSyngo,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKsubmittedForSyngo, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                initialSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKsubmittedForSyngo]
                ?.AlreadyUploaded
        ) {
            this.isIVKsubmittedForSyngo.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS_YES_NA[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKsubmittedForSyngo
                    ].Previousresponse
                ],
            );
        }

        this.isLeonardoLicenseKeyRemoved = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isLeonardoLicenseKeyRemoved,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isLeonardoLicenseKeyRemoved, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                initialSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isLeonardoLicenseKeyRemoved
            ]?.AlreadyUploaded
        ) {
            this.isLeonardoLicenseKeyRemoved.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS_YES_NA[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isLeonardoLicenseKeyRemoved
                    ].Previousresponse
                ],
            );
        }

        this.isSiemensSoldOEMEquipment = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                initialSelection: -1,
            }),
        );
        this.soldOEMEquipmentList = [];
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment]
                .Previousresponse.length > 0
        ) {
            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment
                ].Previousresponse === INSTALLATION_PAPERWORK_OPTIONS_YES_NA[ConfigConstants.optionNA]
            ) {
                this.isSiemensSoldOEMEquipment.selectByDisplayName(ConfigConstants.optionNA);
            } else {
                this.isSiemensSoldOEMEquipment.selectByDisplayName(ConfigConstants.optionYes);
                this.soldOEMEquipmentList =
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment
                    ].Previousresponse.split('|');
            }
        }

        this.isPaceStartupAttached = this.getTextModelForFormAndDocboxQuestion(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPaceStartupAttached,
            this.startupFormSubmitStatus,
        );

        this.attachIQAPReport = this.getTextModelForFormAndDocboxQuestion(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachIQAPReport,
            this.iqapReportSubmitStatus,
        );

        this.attachFDA2579Form = new Fda2579FormModel(notification);
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachFDA2579Form]
                ?.AlreadyUploaded
        ) {
            this.isFDA2579FormCompletedOnPreviousTask =
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachFDA2579Form]
                    ?.Previousresponse === INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes];
            this.attachFDA2579FormCompleted.set(this.isFDA2579FormCompletedOnPreviousTask);
        }

        this.isIseCseMetricFormCompleted = new DisplayDecorator<BooleanModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIseCseMetricFormComplete,
            new BooleanModel(
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIseCseMetricFormComplete,
                false,
                new BooleanModelOptions({ showLabel: false }),
            ),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIseCseMetricFormComplete]
                ?.AlreadyUploaded
        ) {
            this.isIseCseMetricFormCompleted.value =
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIseCseMetricFormComplete
                ]?.Previousresponse === INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes];
        }

        this.heliumLevelArrival = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival,
            '',
            new TextModelOptions({ required: true, pattern: HELIUM_LEVEL_PATTERN, placeholder: '00.0% or 0000.0L' }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival]
                ?.Previousresponse
        ) {
            this.heliumLevelArrival.value =
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival]
                    ?.Previousresponse;
        }

        // Chiller section.
        this.isChillerFlModelSerialGiven = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNA,
            ]),
            new RadioModelOptions({
                required: true,
                initialSelection: -1,
            }),
        );
        this.chillerFunctionalLocation = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven,
            '',
            new TextModelOptions({ required: true }),
        );
        this.chillerMakeModelNumber = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven,
            '',
            new TextModelOptions({ required: true }),
        );
        this.chillerSerialNumber = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven,
            '',
            new TextModelOptions({ required: true }),
        );
        if (
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven
            ]?.AlreadyUploaded &&
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven
            ]?.Previousresponse
        ) {
            const response =
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven
                ].Previousresponse;
            if (response === INSTALLATION_PAPERWORK_OPTIONS_YES_NA[ConfigConstants.optionNA]) {
                this.isChillerFlModelSerialGiven.selectByDisplayName(ConfigConstants.optionNA);
            } else {
                this.isChillerFlModelSerialGiven.selectByDisplayName(ConfigConstants.optionYes);
                const responseList = response.split('|');
                if (responseList.length === 3) {
                    this.chillerFunctionalLocation.value = responseList[0].trim();
                    this.chillerMakeModelNumber.value = responseList[1].trim();
                    this.chillerSerialNumber.value = responseList[2].trim();
                }
            }
        }

        this.heliumLevelCompletion = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelCompletion,
            '',
            new TextModelOptions({ required: true, pattern: HELIUM_LEVEL_PATTERN, placeholder: '00.0% or 0000.0L' }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelCompletion]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelCompletion]
                ?.Previousresponse
        ) {
            this.heliumLevelCompletion.value =
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelCompletion]
                    ?.Previousresponse;
        }

        this.magneticCurrent = new NumberModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.magneticCurrent,
            undefined,
            new NumberModelOptions({
                required: true,
                step: 0.01,
                placeholder: translate.instant('INSTALLATION_PAPERWORK_MAGNETIC_CURRENT_HINT'),
                pattern: MAGNETIC_CURRENT_PATTERN,
                minValue: 0,
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.magneticCurrent]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.magneticCurrent]
                ?.Previousresponse
        ) {
            this.magneticCurrent.value = parseFloat(
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.magneticCurrent]
                    ?.Previousresponse,
            );
        }

        this.isMagnetRampedDownAtRequest = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMagnetRampedDownAtRequest,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMagnetRampedDownAtRequest, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
            ]),
            new RadioModelOptions({
                required: true,
                initialSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMagnetRampedDownAtRequest
            ]?.AlreadyUploaded
        ) {
            this.isMagnetRampedDownAtRequest.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMagnetRampedDownAtRequest
                    ].Previousresponse
                ],
            );
        }

        this.isHeliumTrackerDataEntered = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isHeliumTrackerDataEntered,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isHeliumTrackerDataEntered, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
            ]),
            new RadioModelOptions({
                required: true,
                initialSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isHeliumTrackerDataEntered]
                ?.AlreadyUploaded
        ) {
            this.isHeliumTrackerDataEntered.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isHeliumTrackerDataEntered
                    ].Previousresponse
                ],
            );
        }

        const syngoSoftwareEntries = [
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SYNGO_SOFTWARE_CLASSIC'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware,
                    ConfigConstants.INSTALLATION_PAPERWORK_SYNGO_SOFTWARE.classic,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SYNGO_SOFTWARE_NATIVE'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware,
                    ConfigConstants.INSTALLATION_PAPERWORK_SYNGO_SOFTWARE.native,
                ),
            ),
        ];
        this.syngoClassicOrNativeSoftware = new DropdownModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware,
            syngoSoftwareEntries,
            new DropdownModelOptions({
                required: true,
            }),
        );
        if (
            this.installationPaperworkQuestions[
                ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware
            ]?.AlreadyUploaded
        ) {
            this.syngoClassicOrNativeSoftware.selectByValue(
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware
                ].Previousresponse,
            );
        }

        this.attachSyngoSafetyRelatedReport = this.getTextModelForFormAndDocboxQuestion(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachSyngoSafetyRelatedReport,
            this.syngoSafetyReportSubmitStatus,
        );

        this.preRampPhotos = {
            fileNames: [],
            options: {
                completed: signal(false),
                required: signal(true),
            },
        };
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.preRampPhotos]
                ?.AlreadyUploaded
        ) {
            this.preRampPhotos.options.completed.set(
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.preRampPhotos]
                    .Previousresponse === ConfigConstants.OPTION_1_YES,
            );
        }

        this.isMRDoorSwitchOperational = new RadioModel<TextModel>(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMRDoorSwitchOperational,
            radioOptionGroup(ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMRDoorSwitchOperational, [
                ConfigConstants.optionYes,
                ConfigConstants.optionNo,
            ]),
            new RadioModelOptions({
                initialSelection: -1,
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMRDoorSwitchOperational]
                ?.AlreadyUploaded
        ) {
            this.isMRDoorSwitchOperational.selectByDisplayName(
                INSTALLATION_PAPERWORK_OPTIONS[
                    this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMRDoorSwitchOperational
                    ].Previousresponse
                ],
            );
        }

        this.startupReportMR = this.getTextModelForFormAndDocboxQuestion(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupReportMR,
            this.startupReportMRSubmitStatus,
        );

        this.installationReport = this.getTextModelForFormAndDocboxQuestion(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.installationReport,
            this.installationReportSubmitStatus,
        );

        const siteTypeEntries = [
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SITE_TYPE_NEW'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType,
                    ConfigConstants.INSTALLATION_PAPERWORK_SITE_TYPE.newConstruction,
                ),
            ),
            new DisplayDecorator<TextModel>(
                translate.instant('INSTALLATION_PAPERWORK_SITE_TYPE_EXISTING'),
                new TextModel(
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType,
                    ConfigConstants.INSTALLATION_PAPERWORK_SITE_TYPE.existing,
                ),
            ),
        ];
        this.siteType = new DropdownModel(ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType, siteTypeEntries);
        this.siteTypeComment = new TextModel(
            ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType,
            '',
            new TextModelOptions({
                placeholder: translate.instant('INSTALLATION_PAPERWORK_SITE_TYPE_COMMENT_HINT'),
            }),
        );
        if (
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType]
                ?.AlreadyUploaded &&
            this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType].Previousresponse
        ) {
            const response =
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType
                ].Previousresponse.split('|');
            this.siteType.selectByValue(response[0]);
            if (response.length > 1) {
                this.siteTypeComment.value = response[1];
            }
        }

        this.updateModelOptions();
    }

    /**
     * Punchlists arrive from NotificationModel already parsed into whole
     * entries (wrapped lines merged, counters stripped). We just populate the
     * ArrayModel, unless the punchlist is already marked complete.
     *
     * @param code installation paperwork code for punchlists STPL or STPC
     * @param entries list of punchlist items
     * @param isComplete p58 backend status of punchlist
     */
    private buildPunchlist(
        code: ConfigConstants.INSTALLATION_PAPERWORK_CODES,
        entries: string[],
        isComplete: boolean,
    ): ArrayModel<TextModel> {
        const punchlist = new ArrayModel<TextModel>(
            code,
            [],
            new ArrayModelOptions({ maxEntries: PUNCHLIST_MAX_ENTRIES }),
        );
        if (!isComplete) {
            entries.forEach((entry) => this.addPunchlistItem(punchlist, entry));
        }
        return punchlist;
    }

    private setAllElementsToInProgress(): void {
        this.finalDayAttachments.options.required.set(false);
        this.preRampPhotos.options.required.set(false);
        this.isIVKVerified = assignModelOptions(this.isIVKVerified, {
            ...this.isIVKVerified.options,
            required: false,
        });
        this.isSerialNumberDifferentThanIVK = assignModelOptions(this.isSerialNumberDifferentThanIVK, {
            ...this.isSerialNumberDifferentThanIVK.options,
            required: false,
        });
        this.differentSerialNumberThanIVKLongText = assignModelOptions(this.differentSerialNumberThanIVKLongText, {
            ...this.differentSerialNumberThanIVKLongText.options,
            required: false,
        });
        this.isSRSVerifiedBySrsTse = assignModelOptions(this.isSRSVerifiedBySrsTse, {
            ...this.isSRSVerifiedBySrsTse.options,
            required: false,
        });
        this.isDICOMProvided = assignModelOptions(this.isDICOMProvided, {
            ...this.isDICOMProvided.options,
            required: false,
        });
        this.isSafetyUICompleted = assignModelOptions(this.isSafetyUICompleted, {
            ...this.isSafetyUICompleted.options,
            required: false,
        });
        this.cseHandoverMethod = assignModelOptions(this.cseHandoverMethod, {
            ...this.cseHandoverMethod.options,
            required: false,
        });
        this.cseHandoverName = assignModelOptions(this.cseHandoverName, {
            ...this.cseHandoverName.options,
            required: false,
        });
        this.cseHandoverDate = assignModelOptions(this.cseHandoverDate, {
            ...this.cseHandoverDate.options,
            required: false,
        });
        this.isArtisLicenseKeyRemoved = assignModelOptions(this.isArtisLicenseKeyRemoved, {
            ...this.isArtisLicenseKeyRemoved.options,
            required: false,
        });
        this.isIVKsubmittedForSyngo = assignModelOptions(this.isIVKsubmittedForSyngo, {
            ...this.isIVKsubmittedForSyngo.options,
            required: false,
        });
        this.isLeonardoLicenseKeyRemoved = assignModelOptions(this.isLeonardoLicenseKeyRemoved, {
            ...this.isLeonardoLicenseKeyRemoved.options,
            required: false,
        });
        this.isSiemensSoldOEMEquipment = assignModelOptions(this.isSiemensSoldOEMEquipment, {
            ...this.isSiemensSoldOEMEquipment.options,
            required: false,
        });
        this.isPaceStartupAttached = assignModelOptions(this.isPaceStartupAttached, {
            ...this.isPaceStartupAttached.options,
            required: false,
        });
        this.attachIQAPReport = assignModelOptions(this.attachIQAPReport, {
            ...this.attachIQAPReport.options,
            required: false,
        });
        this.attachFDA2579Form = assignModelOptions(this.attachFDA2579Form, {
            ...this.attachFDA2579Form.options,
            required: false,
        });
        this.isIseCseMetricFormCompleted = assignModelOptions(this.isIseCseMetricFormCompleted, {
            ...this.isIseCseMetricFormCompleted.options,
            required: false,
        });
        this.isChillerFlModelSerialGiven = assignModelOptions(this.isChillerFlModelSerialGiven, {
            ...this.isChillerFlModelSerialGiven.options,
            required: false,
        });
        const isHeliumLevelArrivalRequired =
            !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival]
                ?.Required &&
            this.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes &&
            this.isSystemReadyForStartupAndDayOne.getSelectedIndex() === 0;
        // Helium levels need to be checked upon arrival when day 1 is true and day 1 pictures are uploaded.
        this.heliumLevelArrival = assignModelOptions(this.heliumLevelArrival, {
            ...this.heliumLevelArrival.options,
            required: isHeliumLevelArrivalRequired,
        });
        this.heliumLevelCompletion = assignModelOptions(this.heliumLevelCompletion, {
            ...this.heliumLevelCompletion.options,
            required: false,
        });
        this.magneticCurrent = assignModelOptions(this.magneticCurrent, {
            ...this.magneticCurrent.options,
            required: false,
        });
        this.isMagnetRampedDownAtRequest = assignModelOptions(this.isMagnetRampedDownAtRequest, {
            ...this.isMagnetRampedDownAtRequest.options,
            required: false,
        });
        this.isHeliumTrackerDataEntered = assignModelOptions(this.isHeliumTrackerDataEntered, {
            ...this.isHeliumTrackerDataEntered.options,
            required: false,
        });
        this.syngoClassicOrNativeSoftware = assignModelOptions(this.syngoClassicOrNativeSoftware, {
            ...this.syngoClassicOrNativeSoftware.options,
            required: false,
        });
        this.attachSyngoSafetyRelatedReport = assignModelOptions(this.attachSyngoSafetyRelatedReport, {
            ...this.attachSyngoSafetyRelatedReport.options,
            required: false,
        });
        this.isMRDoorSwitchOperational = assignModelOptions(this.isMRDoorSwitchOperational, {
            ...this.isMRDoorSwitchOperational.options,
            required: false,
        });

        this.startupReportMR = assignModelOptions(this.startupReportMR, {
            ...this.startupReportMR.options,
            required: false,
        });

        this.installationReport = assignModelOptions(this.installationReport, {
            ...this.installationReport.options,
            required: false,
        });

        this.siteType = assignModelOptions(this.siteType, {
            ...this.siteType.options,
            required: false,
        });
    }

    private setAllElementsToRequired(): void {
        this.finalDayAttachments.options.required.set(
            !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.finalDayAttachments]
                ?.Required,
        );
        this.preRampPhotos.options.required.set(
            !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.preRampPhotos]?.Required,
        );
        this.isIVKVerified = assignModelOptions(this.isIVKVerified, {
            ...this.isIVKVerified.options,
            required: true,
        });
        this.isSerialNumberDifferentThanIVK = assignModelOptions(this.isSerialNumberDifferentThanIVK, {
            ...this.isSerialNumberDifferentThanIVK.options,
            required: true,
        });
        this.differentSerialNumberThanIVKLongText = assignModelOptions(this.differentSerialNumberThanIVKLongText, {
            ...this.differentSerialNumberThanIVKLongText.options,
            required: true,
        });
        this.isSRSVerifiedBySrsTse = assignModelOptions(this.isSRSVerifiedBySrsTse, {
            ...this.isSRSVerifiedBySrsTse.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSRSVerifiedBySrsTse
                ]?.Required,
        });
        this.isDICOMProvided = assignModelOptions(this.isDICOMProvided, {
            ...this.isDICOMProvided.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isDICOMProvided]
                    ?.Required,
        });
        this.isSafetyUICompleted = assignModelOptions(this.isSafetyUICompleted, {
            ...this.isSafetyUICompleted.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSafetyUICompleted]
                    ?.Required,
        });
        this.cseHandoverMethod = assignModelOptions(this.cseHandoverMethod, {
            ...this.cseHandoverMethod.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod]
                    ?.Required,
        });
        this.cseHandoverName = assignModelOptions(this.cseHandoverName, {
            ...this.cseHandoverName.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverName]
                    ?.Required,
        });
        this.cseHandoverDate = assignModelOptions(this.cseHandoverDate, {
            ...this.cseHandoverDate.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverDate]
                    ?.Required,
        });
        this.isArtisLicenseKeyRemoved = assignModelOptions(this.isArtisLicenseKeyRemoved, {
            ...this.isArtisLicenseKeyRemoved.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isArtisLicenseKeyRemoved
                ]?.Required,
        });
        this.isIVKsubmittedForSyngo = assignModelOptions(this.isIVKsubmittedForSyngo, {
            ...this.isIVKsubmittedForSyngo.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKsubmittedForSyngo
                ]?.Required,
        });
        this.isLeonardoLicenseKeyRemoved = assignModelOptions(this.isLeonardoLicenseKeyRemoved, {
            ...this.isLeonardoLicenseKeyRemoved.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isLeonardoLicenseKeyRemoved
                ]?.Required,
        });
        this.isSiemensSoldOEMEquipment = assignModelOptions(this.isSiemensSoldOEMEquipment, {
            ...this.isSiemensSoldOEMEquipment.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment
                ]?.Required,
        });
        this.isPaceStartupAttached = assignModelOptions(this.isPaceStartupAttached, {
            ...this.isPaceStartupAttached.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPaceStartupAttached
                ]?.Required,
        });
        this.attachIQAPReport = assignModelOptions(this.attachIQAPReport, {
            ...this.attachIQAPReport.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachIQAPReport]
                    ?.Required,
        });
        this.attachFDA2579Form = assignModelOptions(this.attachFDA2579Form, {
            ...this.attachFDA2579Form.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachFDA2579Form]
                    ?.Required,
        });
        this.isIseCseMetricFormCompleted = assignModelOptions(this.isIseCseMetricFormCompleted, {
            ...this.isIseCseMetricFormCompleted.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIseCseMetricFormComplete
                ]?.Required,
        });
        this.isChillerFlModelSerialGiven = assignModelOptions(this.isChillerFlModelSerialGiven, {
            ...this.isChillerFlModelSerialGiven.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven
                ]?.Required,
        });
        this.heliumLevelArrival = assignModelOptions(this.heliumLevelArrival, {
            ...this.heliumLevelArrival.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival]
                    ?.Required,
        });
        this.heliumLevelCompletion = assignModelOptions(this.heliumLevelCompletion, {
            ...this.heliumLevelCompletion.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelCompletion
                ]?.Required,
        });
        this.magneticCurrent = assignModelOptions(this.magneticCurrent, {
            ...this.magneticCurrent.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.magneticCurrent]
                    ?.Required,
        });
        this.isMagnetRampedDownAtRequest = assignModelOptions(this.isMagnetRampedDownAtRequest, {
            ...this.isMagnetRampedDownAtRequest.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMagnetRampedDownAtRequest
                ]?.Required,
        });
        this.isHeliumTrackerDataEntered = assignModelOptions(this.isHeliumTrackerDataEntered, {
            ...this.isHeliumTrackerDataEntered.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isHeliumTrackerDataEntered
                ]?.Required,
        });
        this.syngoClassicOrNativeSoftware = assignModelOptions(this.syngoClassicOrNativeSoftware, {
            ...this.syngoClassicOrNativeSoftware.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware
                ]?.Required,
        });
        this.attachSyngoSafetyRelatedReport = assignModelOptions(this.attachSyngoSafetyRelatedReport, {
            ...this.attachSyngoSafetyRelatedReport.options,
            required:
                this.syngoClassicOrNativeSoftware.selectedEntry?.value ===
                INSTALLATION_PAPERWORK_SYNGO_SOFTWARE.classic,
        });
        this.isMRDoorSwitchOperational = assignModelOptions(this.isMRDoorSwitchOperational, {
            ...this.isMRDoorSwitchOperational.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMRDoorSwitchOperational
                ]?.Required,
        });

        this.startupReportMR = assignModelOptions(this.startupReportMR, {
            ...this.startupReportMR.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupReportMR]
                    ?.Required,
        });

        this.installationReport = assignModelOptions(this.installationReport, {
            ...this.installationReport.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.installationReport]
                    ?.Required,
        });

        this.siteType = assignModelOptions(this.siteType, {
            ...this.siteType.options,
            required:
                !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType]?.Required,
        });
    }

    /**
     * Business logic for Installation paperwork.
     *
     * if not lead responsible: - nothing is required and nothing should be submitted in the outModel.
     *
     * if StartupStatus [STST] is "System Startup in progress":
     * - nothing is mandatory, unless Day 1 [IN01] is true. If Day 1 true, then images are required.
     *
     * if StartupStatus [STST] is "System Startup Complete" everything is mandatory. If Day 1 is false, Attachments are hidden and off.
     *
     * if Startup Status [STST] is "System Startup Complete - With Punchlist": same as "System Startup Complete" ?
     *
     */
    public updateModelOptions(): void {
        // If day one is selected, day one attachments must be uploaded.
        this.dayOneAttachments.options.required.set(
            this.isSystemReadyForStartupAndDayOne.getSelectedIndex() === 0 &&
                this.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes,
        );
        // If the chiller is selected, then we always need input, no matter the other selections.
        this.chillerFunctionalLocation = assignModelOptions(this.chillerFunctionalLocation, {
            ...this.chillerFunctionalLocation.options,
            required:
                this.isChillerFlModelSerialGiven.getSelectedIndex() === 0 &&
                this.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes,
        });
        this.chillerMakeModelNumber = assignModelOptions(this.chillerMakeModelNumber, {
            ...this.chillerMakeModelNumber.options,
            required:
                this.isChillerFlModelSerialGiven.getSelectedIndex() === 0 &&
                this.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes,
        });
        this.chillerSerialNumber = assignModelOptions(this.chillerSerialNumber, {
            ...this.chillerSerialNumber.options,
            required:
                this.isChillerFlModelSerialGiven.getSelectedIndex() === 0 &&
                this.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes,
        });
        // Only show questions to person lead responsible.
        if (this.isPersonLeadResponsible.selectedEntry.value !== ConfigConstants.optionYes) {
            this.setAllElementsToInProgress();
            this.isSystemReadyForStartupAndDayOne = assignModelOptions(this.isSystemReadyForStartupAndDayOne, {
                ...this.isSystemReadyForStartupAndDayOne.options,
                required: false,
                invalidSelection: -1,
            });
            return;
        }

        this.isSystemReadyForStartupAndDayOne = assignModelOptions(this.isSystemReadyForStartupAndDayOne, {
            ...this.isSystemReadyForStartupAndDayOne.options,
            required:
                !!this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSystemReadyForStartupAndDayOne
                ]?.Required,
            invalidSelection: 2,
        });

        switch (this.startupStatus.selectedEntry?.value) {
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupComplete:
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists:
                this.setAllElementsToRequired();
                break;
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupInProgress:
            default:
                this.setAllElementsToInProgress();
                break;
        }
    }

    public toJSON(): JsonConfirmationInstallationPaperworkModel {
        return {
            installationPaperworkQuestions: this.installationPaperworkQuestions,
            siemensPunchList: this.siemensPunchlist,
            customerPunchList: this.customerPunchlist,
            startupStatus: this.startupStatus.selectedEntry?.value ?? '',
            isPersonLeadResponsible: this.isPersonLeadResponsible.selectedEntry.value,
            isSystemReadyForStartupAndDayOne: this.isSystemReadyForStartupAndDayOne.getSelectedIndex(),
            dayOneAttachments: this.dayOneAttachments.fileNames,
            isIVKVerified: this.isIVKVerified.value,
            isSerialNumberDifferentThanIVK: this.isSerialNumberDifferentThanIVK.getSelectedIndex(),
            differentSerialNumberThanIVKLongText: this.differentSerialNumberThanIVKLongText.value,
            isSRSVerifiedBySrsTse: this.isSRSVerifiedBySrsTse.getSelectedIndex(),
            isDICOMProvided: this.isDICOMProvided.getSelectedIndex(),
            isSafetyUICompleted: this.isSafetyUICompleted.getSelectedIndex(),
            finalDayAttachments: this.finalDayAttachments.fileNames,
            cseHandoverMethod: this.cseHandoverMethod.selectedEntry?.value ?? '',
            cseHandoverName: this.cseHandoverName.textFieldValue,
            cseHandoverDate: this.cseHandoverDate.value?.toISOString() ?? '',
            isArtisLicenseKeyRemoved: this.isArtisLicenseKeyRemoved.getSelectedIndex(),
            isIVKsubmittedForSyngo: this.isIVKsubmittedForSyngo.getSelectedIndex(),
            isLeonardoLicenseKeyRemoved: this.isLeonardoLicenseKeyRemoved.getSelectedIndex(),
            isSiemensSoldOEMEquipment: this.isSiemensSoldOEMEquipment.getSelectedIndex(),
            soldOEMEquipment: this.soldOEMEquipmentList,
            isPaceStartupAttached: this.isPaceStartupAttached.value,
            startupFormSubmitStatus: this.startupFormSubmitStatus(),
            attachIQAPReport: this.attachIQAPReport.value,
            iqapResportSubmitStatus: this.iqapReportSubmitStatus(),
            attachFDA2579Form: this.attachFDA2579Form.toJSON(),
            isIseCseMetricFormCompleted: this.isIseCseMetricFormCompleted.value,
            heliumLevelArrival: this.heliumLevelArrival.value,
            isChillerFlModelSerialGiven: this.isChillerFlModelSerialGiven.getSelectedIndex(),
            chillerFunctionalLocation: this.chillerFunctionalLocation.value,
            chillerMakeModelNumber: this.chillerMakeModelNumber.value,
            chillerSerialNumber: this.chillerSerialNumber.value,
            heliumLevelCompletion: this.heliumLevelCompletion.value,
            magneticCurrent: this.magneticCurrent.value,
            isMagnetRampedDownAtRequest: this.isMagnetRampedDownAtRequest.getSelectedIndex(),
            isHeliumTrackerDataEntered: this.isHeliumTrackerDataEntered.getSelectedIndex(),
            syngoClassicOrNativeSoftware: this.syngoClassicOrNativeSoftware.selectedEntry?.value ?? '',
            attachSyngoSafetyRelatedReport: this.attachSyngoSafetyRelatedReport.value,
            syngoSafetyReportSubmitStatus: this.syngoSafetyReportSubmitStatus(),
            preRampPhotos: this.preRampPhotos.fileNames,
            isMRDoorSwitchOperational: this.isMRDoorSwitchOperational.getSelectedIndex(),
            startupReportMR: this.startupReportMR.value,
            startupReportMRSubmitStatus: this.startupReportMRSubmitStatus(),
            installationReport: this.installationReport.value,
            installationReportSubmitStatus: this.installationReportSubmitStatus(),
            siteType: this.siteType.selectedEntry?.value ?? '',
            siteTypeComment: this.siteTypeComment.value ?? '',
        };
    }

    public getOutModel(): ODataConfirmationInstallationPaperworkOutModel {
        const outModel = super.getOutModel();
        const currentDateTime = moment().startOf('second');
        const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));
        const longtextPaperwork: ODataLongtextPaperworkOutModel[] = [];
        const fdaSet: ODataFdaOutModel[] = [];
        const submissionTime = moment().startOf('second');

        // only send the paperwork section if the user is lead responsible
        if (this.startupStatus.selectedEntry) {
            crmCodes.addModel(
                new CrmCodeModel({
                    startDateTime: currentDateTime,
                    endDateTime: currentDateTime,
                    codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                    codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                    code: this.startupStatus.selectedEntry.model.name,
                    codeText: this.startupStatus.selectedEntry.model.value,
                }),
            );

            if (this.isPersonLeadResponsible.selectedEntry.model.value === optionYes) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isPersonLeadResponsible.selectedEntry.model.name,
                        codeText: this.userGid,
                    }),
                );
            }

            // If punchlists are open, add them to the outmodel.
            if (
                this.startupStatus.selectedEntry.value ===
                ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists
            ) {
                longtextPaperwork.push(
                    ...this.buildPunchlistLongtext(this.siemensPunchlist.name, this.siemensPunchlist.value),
                );
                longtextPaperwork.push(
                    ...this.buildPunchlistLongtext(this.customerPunchlist.name, this.customerPunchlist.value),
                );
                if (this.siemensPunchlist.value.length > 0 && this.isSTPLComplete) {
                    this.markPunchlistNotFixed(this.siemensPunchlist.name, crmCodes, submissionTime);
                }
                if (this.customerPunchlist.value.length > 0 && this.isSTPCComplete) {
                    this.markPunchlistNotFixed(this.customerPunchlist.name, crmCodes, submissionTime);
                }
            }
            // If status is startup complete, send an empty punchlist to mark as complete
            if (
                this.startupStatus.selectedEntry.value ===
                ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupComplete
            ) {
                longtextPaperwork.push(...this.buildPunchlistLongtext(this.siemensPunchlist.name, []));
                longtextPaperwork.push(...this.buildPunchlistLongtext(this.customerPunchlist.name, []));
            }
        } else {
            throw new Error('[Confirmation Installation Paperwork] getOutModel: Required entries are not present.');
        }

        if (this.isPersonLeadResponsible.getSelectedIndex() === 0) {
            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSystemReadyForStartupAndDayOne
                ]?.Required &&
                this.isSystemReadyForStartupAndDayOne.selectedEntry.value === optionYes
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isSystemReadyForStartupAndDayOne.name,
                        codeText:
                            INSTALLATION_PAPERWORK_OPTIONS[this.isSystemReadyForStartupAndDayOne.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKVerified]
                    ?.Required &&
                this.isIVKVerified.value
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isIVKVerified.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes],
                    }),
                );
            }
            if (
                this.isSerialNumberDifferentThanIVK.getSelectedIndex() === 0 &&
                this.differentSerialNumberThanIVKLongText.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isSerialNumberDifferentThanIVK.name,
                        codeText: this.differentSerialNumberThanIVKLongText.value,
                    }),
                );
                longtextPaperwork.push({
                    TDID: this.isSerialNumberDifferentThanIVK.name,
                    TextLine: this.differentSerialNumberThanIVKLongText.value,
                    Counter: 1,
                });
            } else if (this.isSerialNumberDifferentThanIVK.getSelectedIndex() === 1) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isSerialNumberDifferentThanIVK.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS_YES_NA['N/A'],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSRSVerifiedBySrsTse]
                    ?.Required &&
                isDefined(this.isSRSVerifiedBySrsTse.selectedEntry)
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isSRSVerifiedBySrsTse.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[this.isSRSVerifiedBySrsTse.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isDICOMProvided]
                    ?.Required &&
                this.isDICOMProvided.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isDICOMProvided.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[this.isDICOMProvided.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSafetyUICompleted]
                    ?.Required &&
                this.isSafetyUICompleted.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isSafetyUICompleted.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS_YES_NA[this.isSafetyUICompleted.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.finalDayAttachments]
                    ?.Required &&
                this.finalDayAttachments.fileNames.length > 0
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: ConfigConstants.INSTALLATION_PAPERWORK_CODES.finalDayAttachments,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.cseHandoverMethod]
                    ?.Required &&
                this.cseHandoverMethod.selectedEntry &&
                this.cseHandoverName.textFieldValue !== '' &&
                isDefined(this.cseHandoverDate.value)
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.cseHandoverMethod.name,
                        codeText: this.cseHandoverMethod.selectedEntry.value,
                    }),
                );
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.cseHandoverName.name,
                        codeText: this.cseHandoverName.textFieldValue,
                    }),
                );
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.cseHandoverDate.name,
                        codeText: this.cseHandoverDate.value.format(PPWK_DATE_FORMAT),
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isArtisLicenseKeyRemoved
                ]?.Required &&
                this.isArtisLicenseKeyRemoved.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isArtisLicenseKeyRemoved.name,
                        codeText:
                            INSTALLATION_PAPERWORK_OPTIONS_YES_NA[this.isArtisLicenseKeyRemoved.selectedEntry.value],
                    }),
                );
            }
            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIVKsubmittedForSyngo]
                    ?.Required &&
                this.isIVKsubmittedForSyngo.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isIVKsubmittedForSyngo.name,
                        codeText:
                            INSTALLATION_PAPERWORK_OPTIONS_YES_NA[this.isIVKsubmittedForSyngo.selectedEntry.value],
                    }),
                );
            }
            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isLeonardoLicenseKeyRemoved
                ]?.Required &&
                this.isLeonardoLicenseKeyRemoved.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isLeonardoLicenseKeyRemoved.name,
                        codeText:
                            INSTALLATION_PAPERWORK_OPTIONS_YES_NA[this.isLeonardoLicenseKeyRemoved.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSiemensSoldOEMEquipment
                ]?.Required &&
                this.isSiemensSoldOEMEquipment.getSelectedIndex() > -1
            ) {
                if (this.isSiemensSoldOEMEquipment.getSelectedIndex() === 0 && this.soldOEMEquipmentList.length > 0) {
                    crmCodes.addModel(
                        new CrmCodeModel({
                            startDateTime: currentDateTime,
                            endDateTime: currentDateTime,
                            codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                            codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                            code: this.isSiemensSoldOEMEquipment.name,
                            codeText: this.soldOEMEquipmentList.join('|'),
                        }),
                    );
                    longtextPaperwork.push({
                        TDID: this.isSiemensSoldOEMEquipment.name,
                        TextLine: this.soldOEMEquipmentList.join('|'),
                        Counter: 1,
                    });
                } else {
                    crmCodes.addModel(
                        new CrmCodeModel({
                            startDateTime: currentDateTime,
                            endDateTime: currentDateTime,
                            codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                            codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                            code: this.isSiemensSoldOEMEquipment.name,
                            codeText:
                                INSTALLATION_PAPERWORK_OPTIONS_YES_NA[
                                    this.isSiemensSoldOEMEquipment.selectedEntry.value
                                ],
                        }),
                    );
                }
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPaceStartupAttached]
                    ?.Required &&
                this.isPaceStartupAttached.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isPaceStartupAttached.name,
                        codeText: this.isPaceStartupAttached.value.substring(0, CRM_CODE_MODEL_VALUE_MAX_STRING_LENGTH),
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachIQAPReport]
                    ?.Required &&
                this.attachIQAPReport.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.attachIQAPReport.name,
                        codeText: this.attachIQAPReport.value.substring(0, CRM_CODE_MODEL_VALUE_MAX_STRING_LENGTH),
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachFDA2579Form]
                    ?.Required &&
                isDefined(this.attachFDA2579Form) &&
                this.attachFDA2579Form.isFda2579FormValid
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachFDA2579Form,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS_YES_NA.Yes,
                    }),
                );
                fdaSet.push({
                    NotificationNo: this.notification.identifier.getQmnum(),
                    TaskNumber: this.notification.identifier.getManum(),
                    Material: this.notification.getMainEquipment()?.equnr.value ?? '',
                    FDAForm: EncodingUtilities.base64EncodeUnicode(
                        JSON.stringify(this.attachFDA2579Form.getOutModel()),
                    ),
                });
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isIseCseMetricFormComplete
                ]?.Required &&
                this.isIseCseMetricFormCompleted.value
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isIseCseMetricFormCompleted.name,
                        codeText: this.isIseCseMetricFormCompleted.value
                            ? INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes]
                            : INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionNo],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelArrival]
                    ?.Required &&
                this.heliumLevelArrival.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.heliumLevelArrival.name,
                        codeText: this.heliumLevelArrival.value,
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isChillerFlModelSerialGiven
                ]?.Required &&
                this.isChillerFlModelSerialGiven.getSelectedIndex() > -1
            ) {
                if (this.isChillerFlModelSerialGiven.getSelectedIndex() === 0) {
                    crmCodes.addModel(
                        new CrmCodeModel({
                            startDateTime: currentDateTime,
                            endDateTime: currentDateTime,
                            codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                            codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                            code: this.isChillerFlModelSerialGiven.name,
                            codeText: [
                                this.chillerFunctionalLocation.value,
                                this.chillerMakeModelNumber.value,
                                this.chillerSerialNumber.value,
                            ].join('|'),
                        }),
                    );
                    longtextPaperwork.push({
                        TDID: this.isChillerFlModelSerialGiven.name,
                        TextLine: [
                            this.chillerFunctionalLocation.value,
                            this.chillerMakeModelNumber.value,
                            this.chillerSerialNumber.value,
                        ].join('|'),
                        Counter: 1,
                    });
                } else {
                    crmCodes.addModel(
                        new CrmCodeModel({
                            startDateTime: currentDateTime,
                            endDateTime: currentDateTime,
                            codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                            codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                            code: this.isChillerFlModelSerialGiven.name,
                            codeText: INSTALLATION_PAPERWORK_OPTIONS_YES_NA[ConfigConstants.optionNA],
                        }),
                    );
                }
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.heliumLevelCompletion]
                    ?.Required &&
                this.heliumLevelCompletion.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.heliumLevelCompletion.name,
                        codeText: this.heliumLevelCompletion.value,
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.magneticCurrent]
                    ?.Required &&
                isDefined(this.magneticCurrent.value)
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.magneticCurrent.name,
                        codeText: this.magneticCurrent.value.toString(),
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMagnetRampedDownAtRequest
                ]?.Required &&
                this.isMagnetRampedDownAtRequest.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isMagnetRampedDownAtRequest.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[this.isMagnetRampedDownAtRequest.selectedEntry.value],
                    }),
                );
            }
            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isHeliumTrackerDataEntered
                ]?.Required &&
                this.isHeliumTrackerDataEntered.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isHeliumTrackerDataEntered.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[this.isHeliumTrackerDataEntered.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.syngoClassicOrNativeSoftware
                ]?.Required &&
                this.syngoClassicOrNativeSoftware.selectedEntry
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.syngoClassicOrNativeSoftware.name,
                        codeText: this.syngoClassicOrNativeSoftware.selectedEntry.value,
                    }),
                );

                if (
                    this.syngoClassicOrNativeSoftware.selectedEntry.value ===
                        INSTALLATION_PAPERWORK_SYNGO_SOFTWARE.classic &&
                    this.attachSyngoSafetyRelatedReport.value !== ''
                ) {
                    crmCodes.addModel(
                        new CrmCodeModel({
                            startDateTime: currentDateTime,
                            endDateTime: currentDateTime,
                            codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                            codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                            code: this.attachSyngoSafetyRelatedReport.name,
                            codeText: this.attachSyngoSafetyRelatedReport.value.substring(
                                0,
                                CRM_CODE_MODEL_VALUE_MAX_STRING_LENGTH,
                            ),
                        }),
                    );
                }
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.preRampPhotos]
                    ?.Required &&
                this.preRampPhotos.fileNames.length > 0
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: ConfigConstants.INSTALLATION_PAPERWORK_CODES.preRampPhotos,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[ConfigConstants.optionYes],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[
                    ConfigConstants.INSTALLATION_PAPERWORK_CODES.isMRDoorSwitchOperational
                ]?.Required &&
                this.isMRDoorSwitchOperational.getSelectedIndex() > -1
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.isMRDoorSwitchOperational.name,
                        codeText: INSTALLATION_PAPERWORK_OPTIONS[this.isMRDoorSwitchOperational.selectedEntry.value],
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupReportMR]
                    ?.Required &&
                this.startupReportMR.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.startupReportMR.name,
                        codeText: this.startupReportMR.value.substring(0, CRM_CODE_MODEL_VALUE_MAX_STRING_LENGTH),
                    }),
                );
            }
            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.installationReport]
                    ?.Required &&
                this.installationReport.value !== ''
            ) {
                crmCodes.addModel(
                    new CrmCodeModel({
                        startDateTime: currentDateTime,
                        endDateTime: currentDateTime,
                        codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                        codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                        code: this.installationReport.name,
                        codeText: this.installationReport.value.substring(0, CRM_CODE_MODEL_VALUE_MAX_STRING_LENGTH),
                    }),
                );
            }

            if (
                this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.siteType]?.Required &&
                this.siteType.selectedEntry
            ) {
                if (
                    this.siteType.selectedEntry.value === INSTALLATION_PAPERWORK_SITE_TYPE.existing &&
                    this.siteTypeComment.value.length > 0
                ) {
                    longtextPaperwork.push({
                        TDID: this.siteTypeComment.name,
                        TextLine: `${this.siteType.selectedEntry.value}|${this.siteTypeComment.value}`,
                        Counter: 1,
                    });
                } else {
                    crmCodes.addModel(
                        new CrmCodeModel({
                            startDateTime: currentDateTime,
                            endDateTime: currentDateTime,
                            codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                            codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                            code: this.siteType.name,
                            codeText: this.siteType.selectedEntry.value,
                        }),
                    );
                }
            }
        }
        merge(outModel, crmCodes.getOutModel(), { CrmLongTextPPWKSet: longtextPaperwork }, { FDASet: fdaSet });
        return <ODataConfirmationInstallationPaperworkOutModel>outModel;
    }

    /**
     * If a punchlist has entries, send empty codeText so the backend short
     * text is not set to 'Fixed'. (An empty punchlist is left to be marked
     * complete by the blank line emitted in buildPunchlistLongtext.)
     *
     * @param code CrmCode string
     * @param crmCodes Colleciton of all CrmCodes to be sent
     * @param submissionTime timestamp of sending
     */
    private markPunchlistNotFixed(code: string, crmCodes: ArrayModel<CrmCodeModel>, submissionTime: Moment): void {
        crmCodes.addModel(
            new CrmCodeModel({
                codeType: INSTALLATION_PAPERSET_CODE_TYPE,
                codeGroup: INSTALLATION_PAPERSET_CODE_GROUP,
                code,
                codeText: '',
                startDateTime: submissionTime,
                endDateTime: submissionTime,
            }),
        );
    }

    /**
     * Serialises one punchlist into GW longtext lines. Contract: one line per
     * entry with a 1-based Counter — GW renders that counter back as the " N : "
     * prefix that parsePunchlistLines (punchlist-utilities) strips on read. An
     * empty punchlist is sent as a single blank line so the backend marks it
     * complete.
     *
     * @param code The punchlist TDID (siemens/customer punchlist code).
     * @param entries The entry texts, without any counter prefix.
     * @returns The longtext out-model lines for this punchlist.
     */
    private buildPunchlistLongtext(code: string, entries: TextModel[]): ODataLongtextPaperworkOutModel[] {
        if (entries.length === 0) {
            return [{ TDID: code, TextLine: '', Counter: 1 }];
        }
        return entries.map((text, index) => ({ TDID: code, TextLine: text.value, Counter: index + 1 }));
    }

    public addPunchlistItem(model: ArrayModel<TextModel>, value = ''): void {
        model.addModel(
            new TextModel(
                model.name,
                value,
                new TextModelOptions({
                    maxLength: PUNCHLIST_MAX_STRING_LENGTH,
                    minLength: 3,
                    required: true,
                }),
            ),
        );
    }

    // Returns true when every required IN15/IN30/IN31 form or docbox upload has final status.
    // Returns true when none of IN15/IN30/IN31 are required — there's nothing left to complete.
    public get areAllInstallationPaperworkItemsInFinalStatus(): boolean {
        const in15Required =
            !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPaceStartupAttached]
                ?.Required;
        const in30Required =
            !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupReportMR]
                ?.Required;
        const in31Required =
            !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.installationReport]
                ?.Required;
        if (in15Required && this.startupFormSubmitStatus() !== ConfigConstants.DOCUMENT_STATUS_FINAL) {
            return false;
        }
        if (in30Required && this.startupReportMRSubmitStatus() !== ConfigConstants.DOCUMENT_STATUS_FINAL) {
            return false;
        }
        if (in31Required && this.installationReportSubmitStatus() !== ConfigConstants.DOCUMENT_STATUS_FINAL) {
            return false;
        }
        return true;
    }

    public generateFollowUpShortText(): INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT {
        switch (this.startupStatus.selectedEntry?.value) {
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists:
                return this.areAllInstallationPaperworkItemsInFinalStatus
                    ? ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_PUNCHLIST
                    : ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_PAPERWORK_INCOMPLETE;
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupInProgress:
                return ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_IN_PROGRESS;
            case ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupComplete:
                // If lead responsible is No, skip the paperwork check — no follow-up required.
                if (this.isPersonLeadResponsible.selectedEntry?.value === ConfigConstants.optionNo) {
                    return ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_EMPTY;
                }
                // If IN15, IN30 or IN31 is required and not submitted with status final, a follow-up should be generated.
                const in15NotComplete =
                    !!this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPaceStartupAttached
                    ]?.Required && this.startupFormSubmitStatus() !== ConfigConstants.DOCUMENT_STATUS_FINAL;
                const in30NotComplete =
                    !!this.installationPaperworkQuestions[ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupReportMR]
                        ?.Required && this.startupReportMRSubmitStatus() !== ConfigConstants.DOCUMENT_STATUS_FINAL;
                const in31NotComplete =
                    !!this.installationPaperworkQuestions[
                        ConfigConstants.INSTALLATION_PAPERWORK_CODES.installationReport
                    ]?.Required && this.installationReportSubmitStatus() !== ConfigConstants.DOCUMENT_STATUS_FINAL;

                if (in15NotComplete || in30NotComplete || in31NotComplete) {
                    return ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_PAPERWORK_INCOMPLETE;
                }
                return ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_EMPTY;
            default:
                return ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT.FOLLOW_UP_EMPTY;
        }
    }

    /**
     * Function generates a new text model for a given questions that requires a PACE Forms input or Docbox upload
     *
     * @param questionName Question ID
     * @param statusSignal submit status signal
     * @private
     */
    private getTextModelForFormAndDocboxQuestion(
        questionName: ConfigConstants.INSTALLATION_PAPERWORK_CODES,
        statusSignal: WritableSignal<string>,
    ): TextModel {
        const formAndDocboxTextElement = new TextModel(
            questionName,
            '',
            new TextModelOptions({
                required: true,
                readonly: true,
            }),
        );
        const inpwQuestion = this.installationPaperworkQuestions[questionName];
        if (isDefined(inpwQuestion) && inpwQuestion.AlreadyUploaded && inpwQuestion.Previousresponse !== '') {
            formAndDocboxTextElement.value = inpwQuestion.Previousresponse;
            if (inpwQuestion.Previousresponse.indexOf(ConfigConstants.OPTION_1_YES) > -1) {
                this.setStatusFromDocboxFile(inpwQuestion.Previousresponse, statusSignal);
            } else {
                this.setStatusFromForms(inpwQuestion.Previousresponse, statusSignal);
            }
        }
        return formAndDocboxTextElement;
    }

    /**
     * Function that extracts the submit status from a Docbox document title
     * The title is formatted as `1_YES:<SubmitStatus>:<DocumentName>` and will be truncated after 80 characters.
     * The submit status can have a maximum length of 3. Examples include 'F' for final, 'I' for interim, 'H' for handover,
     * 'FNO' for final-not-okay, or 'INO' for interim-not-okay
     *
     * @param documentTitle string from NdpHeaderSet INPW questions
     * @param statusSignal submit status signal
     * @private
     */
    private setStatusFromDocboxFile(documentTitle: string, statusSignal: WritableSignal<string>): void {
        const secondSplit = documentTitle.split(':')[1];
        if (isDefined(secondSplit) && secondSplit.length <= FORMS_MAX_SUBMIT_STATUS_LENGTH && secondSplit.length > 0) {
            statusSignal.set(secondSplit);
        }
    }

    /**
     * Function that extracts the submit status from a PACE Forms document title
     * The title is formatted as `<FormsId>:<SubmitStatus>` and will be truncated after 80 characters.
     * The submit status can have a maximum length of 3. Examples include 'F' for final, 'I' for interim, 'H' for handover,
     * 'FNO' for final-not-okay, or 'INO' for interim-not-okay
     *
     * @param documentTitle string from NdpHeaderSet INPW questions
     * @param statusSignal submit status signal
     * @private
     */
    private setStatusFromForms(documentTitle: string, statusSignal: WritableSignal<string>): void {
        const lastSplit = documentTitle.split(':').at(-1);
        if (isDefined(lastSplit) && lastSplit.length <= FORMS_MAX_SUBMIT_STATUS_LENGTH && lastSplit.length > 0) {
            statusSignal.set(lastSplit);
        }
    }
}

export class ConfirmationInstallationPaperworkModelFromJson extends ConfirmationInstallationPaperworkModel {
    public constructor(data: JsonConfirmationInstallationPaperworkModel, notification: NotificationModel) {
        super(notification);

        if (data.startupStatus !== '') {
            this.startupStatus.selectByValue(data.startupStatus);
        }

        this.siemensPunchlist = new ArrayModelFromJson(TextModelFromJson, data.siemensPunchList);
        this.customerPunchlist = new ArrayModelFromJson(TextModelFromJson, data.customerPunchList);

        this.isPersonLeadResponsible.selectByDisplayName(data.isPersonLeadResponsible);

        if (data.isSystemReadyForStartupAndDayOne > -1) {
            this.isSystemReadyForStartupAndDayOne.selectByIndex(data.isSystemReadyForStartupAndDayOne);
        }
        this.dayOneAttachments.fileNames = data.dayOneAttachments;

        this.finalDayAttachments.fileNames = data.finalDayAttachments;

        this.isIVKVerified.value = data.isIVKVerified;

        if (data.isSerialNumberDifferentThanIVK > -1) {
            this.isSerialNumberDifferentThanIVK.selectByIndex(data.isSerialNumberDifferentThanIVK);
            this.differentSerialNumberThanIVKLongText.value = data.differentSerialNumberThanIVKLongText;
        }

        if (data.isSRSVerifiedBySrsTse > -1) {
            this.isSRSVerifiedBySrsTse.selectByIndex(data.isSRSVerifiedBySrsTse);
        }

        if (data.isDICOMProvided > -1) {
            this.isDICOMProvided.selectByIndex(data.isDICOMProvided);
        }

        this.isSafetyUICompleted.selectByIndex(data.isSafetyUICompleted);

        if (data.cseHandoverMethod !== '') {
            this.cseHandoverMethod.selectByValue(data.cseHandoverMethod);
        }
        if (data.cseHandoverName !== '') {
            this.cseHandoverName.textFieldValue = data.cseHandoverName;
        }
        if (data.cseHandoverDate !== '') {
            this.cseHandoverDate.value = moment(data.cseHandoverDate);
        }

        if (data.isArtisLicenseKeyRemoved > -1) {
            this.isArtisLicenseKeyRemoved.selectByIndex(data.isArtisLicenseKeyRemoved);
        }
        if (data.isIVKsubmittedForSyngo > -1) {
            this.isIVKsubmittedForSyngo.selectByIndex(data.isIVKsubmittedForSyngo);
        }
        if (data.isLeonardoLicenseKeyRemoved > -1) {
            this.isLeonardoLicenseKeyRemoved.selectByIndex(data.isLeonardoLicenseKeyRemoved);
        }

        if (data.isSiemensSoldOEMEquipment > -1) {
            this.isSiemensSoldOEMEquipment.selectByIndex(data.isSiemensSoldOEMEquipment);
        }

        this.soldOEMEquipmentList = data.soldOEMEquipment;

        this.isPaceStartupAttached.value = data.isPaceStartupAttached ?? '';
        this.startupFormSubmitStatus.set(data.startupFormSubmitStatus ?? '');
        this.attachIQAPReport.value = data.attachIQAPReport ?? '';
        this.iqapReportSubmitStatus.set(data.iqapResportSubmitStatus ?? '');

        this.attachFDA2579Form = new Fda2579FormModelFromJson(data.attachFDA2579Form, notification);

        this.isIseCseMetricFormCompleted.value = data.isIseCseMetricFormCompleted;
        this.heliumLevelArrival.value = data.heliumLevelArrival;

        if (data.isChillerFlModelSerialGiven > -1) {
            this.isChillerFlModelSerialGiven.selectByIndex(data.isChillerFlModelSerialGiven);
            if (data.isChillerFlModelSerialGiven === 0) {
                this.chillerFunctionalLocation.value = data.chillerFunctionalLocation;
                this.chillerMakeModelNumber.value = data.chillerMakeModelNumber;
                this.chillerSerialNumber.value = data.chillerSerialNumber;
            }
        }

        this.heliumLevelCompletion.value = data.heliumLevelCompletion;

        if (isDefined(data.magneticCurrent)) {
            this.magneticCurrent.value = data.magneticCurrent;
        }

        if (data.isMagnetRampedDownAtRequest > -1) {
            this.isMagnetRampedDownAtRequest.selectByIndex(data.isMagnetRampedDownAtRequest);
        }
        if (data.isHeliumTrackerDataEntered > -1) {
            this.isHeliumTrackerDataEntered.selectByIndex(data.isHeliumTrackerDataEntered);
        }

        if (data.syngoClassicOrNativeSoftware !== '') {
            this.syngoClassicOrNativeSoftware.selectByValue(data.syngoClassicOrNativeSoftware);
        }

        this.attachSyngoSafetyRelatedReport.value = data.attachSyngoSafetyRelatedReport ?? '';
        this.syngoSafetyReportSubmitStatus.set(data.syngoSafetyReportSubmitStatus ?? '');

        this.preRampPhotos.fileNames = data.preRampPhotos;

        if (data.isMRDoorSwitchOperational > -1) {
            this.isMRDoorSwitchOperational.selectByIndex(data.isMRDoorSwitchOperational);
        }

        this.startupReportMR.value = data.startupReportMR ?? '';
        this.startupReportMRSubmitStatus.set(data.startupReportMRSubmitStatus ?? '');
        this.installationReport.value = data.installationReport ?? '';
        this.installationReportSubmitStatus.set(data.installationReportSubmitStatus ?? '');

        if (data.siteType !== '') {
            this.siteType.selectByValue(data.siteType);
        }

        this.siteTypeComment.value = data.siteTypeComment;
    }
}

export interface JsonConfirmationInstallationPaperworkModel {
    startupStatus: string;
    siemensPunchList: JsonArrayModel;
    customerPunchList: JsonArrayModel;
    isPersonLeadResponsible: string;
    isSystemReadyForStartupAndDayOne: number;
    dayOneAttachments: string[];
    isIVKVerified: boolean;
    isSerialNumberDifferentThanIVK: number;
    differentSerialNumberThanIVKLongText: string;
    isSRSVerifiedBySrsTse: number;
    isDICOMProvided: number;
    isSafetyUICompleted: number;
    finalDayAttachments: string[];
    cseHandoverMethod: string;
    cseHandoverName: string;
    cseHandoverDate: string;
    isArtisLicenseKeyRemoved: number;
    isIVKsubmittedForSyngo: number;
    isLeonardoLicenseKeyRemoved: number;
    isSiemensSoldOEMEquipment: number;
    soldOEMEquipment: string[];
    isPaceStartupAttached: string;
    startupFormSubmitStatus: string;
    attachIQAPReport: string;
    iqapResportSubmitStatus: string;
    attachFDA2579Form: JsonFda2579FormModel;
    isIseCseMetricFormCompleted: boolean;
    heliumLevelArrival: string;
    isChillerFlModelSerialGiven: number;
    chillerFunctionalLocation: string;
    chillerMakeModelNumber: string;
    chillerSerialNumber: string;
    heliumLevelCompletion: string;
    magneticCurrent: number | undefined;
    isMagnetRampedDownAtRequest: number;
    isHeliumTrackerDataEntered: number;
    syngoClassicOrNativeSoftware: string;
    attachSyngoSafetyRelatedReport: string;
    syngoSafetyReportSubmitStatus: string;
    preRampPhotos: string[];
    isMRDoorSwitchOperational: number;
    startupReportMR: string;
    startupReportMRSubmitStatus: string;
    installationReport: string;
    installationReportSubmitStatus: string;
    siteType: string;
    siteTypeComment: string;
    installationPaperworkQuestions: Record<string, ODataPaperworkModel | undefined>;
}

export interface ODataConfirmationInstallationPaperworkOutModel extends ODataObject {
    CrmCodesSet: ODataCrmCodeOutModel[];
    CrmLongTextPPWKSet: ODataLongtextPaperworkOutModel[];
    FDASet: ODataFdaOutModel[];
}

export interface ODataLongtextPaperworkOutModel extends ODataObject {
    TDID: string;
    TextLine: string;
    Counter: number;
}

export interface ODataFdaOutModel extends ODataObject {
    NotificationNo: string;
    TaskNumber: string;
    Material: string;
    FDAForm: string;
}

export interface FileNameList {
    fileNames: string[];
    options: {
        completed: WritableSignal<boolean>;
        required: WritableSignal<boolean>;
    };
}
